import { useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { GroupLogo } from "@/components/GroupLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { lookupGroup } from "@/lib/groups";
import { SignupBonusBanner } from "@/components/SignupBonusBanner";
// PasswordRequirements removed — magic link signup, password set in onboarding
import logoEnglputzeder from "@/assets/logo-englputzeder.png";

export default function Login() {
  const { user, loading, refreshProfile } = useAuth();
  const [params] = useSearchParams();
  const urlCode = (params.get("code") || "").toUpperCase().slice(0, 6);
  const [groupPreview, setGroupPreview] = useState<{ name: string; member_count: number; logo_url: string | null } | null>(null);

  // Login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Signup (Magic Link)
  const [signupEmail, setSignupEmail] = useState("");
  const [signupBusy, setSignupBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Reset
  const [resetEmail, setResetEmail] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  useEffect(() => { document.title = "Anmelden – Englputzeder Tippspiel"; }, []);
  useEffect(() => {
    if (urlCode.length === 6) {
      try { sessionStorage.setItem("pending_invite_code", urlCode); } catch {}
      lookupGroup(urlCode).then(setGroupPreview);
    }
  }, [urlCode]);

  // Cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  if (!loading && user) return <Navigate to="/" replace />;

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      await api.auth.login({ email, password });
      await refreshProfile();
      toast.success("Eingeloggt 🎉");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) toast.error("E-Mail oder Passwort falsch");
        else toast.error(err.message);
      } else {
        toast.error("Login fehlgeschlagen");
      }
    } finally {
      setBusy(false);
    }
  };

  const validSignup = !!signupEmail && /\S+@\S+\.\S+/.test(signupEmail);

  // Bake the invite code into the post-auth redirect so it survives an
  // email→click round-trip where the new tab loses sessionStorage. The
  // server stores the redirect on the MagicLink row and returns it on
  // verify; AuthCallback navigates there, which lands the user at e.g.
  // `/onboarding?code=ABC123` → InviteClaimer fires globally and the
  // claim happens automatically.
  const redirectWithCode = urlCode.length === 6 ? `/onboarding?code=${urlCode}` : "/onboarding";

  const sendSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validSignup) return;
    setSignupBusy(true);
    try {
      await api.auth.requestMagic({
        email: signupEmail,
        purpose: "signup",
        redirect: redirectWithCode,
      });
      setSent(true);
      setResendCooldown(30);
      toast.success("Magic Link gesendet 📬");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Senden");
    } finally {
      setSignupBusy(false);
    }
  };

  const resend = async () => {
    if (resendCooldown > 0 || !signupEmail) return;
    setResendCooldown(30);
    try {
      await api.auth.requestMagic({ email: signupEmail, purpose: "signup", redirect: redirectWithCode });
      toast.success("Mail nochmal gesendet 📬");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Senden");
    }
  };

  const sendReset = async () => {
    if (!resetEmail) return;
    setResetBusy(true);
    try {
      await api.auth.requestReset(resetEmail);
      toast.success("Reset-Link gesendet 📬");
      setResetOpen(false);
      setResetEmail("");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Senden");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-hero text-primary-foreground flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm bg-card text-card-foreground rounded-lg shadow-pop p-6"
        >
          <div className="text-center mb-6">
            <img
              src={logoEnglputzeder}
              alt="Englputzeder"
              className="h-9 w-auto mx-auto mb-3"
            />
            <h1 className="font-display font-extrabold text-4xl text-foreground">
              Tipp<span className="bg-accent text-accent-foreground px-1.5 rounded-md">spiel</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-2">WM 2026 · alle 104 Spiele · gewinne mit deiner Tippgruppe</p>
          </div>

          <div className="mb-4">
            <SignupBonusBanner variant="hero" />
          </div>

          {groupPreview && (
            <div className="rounded-md bg-accent/15 border-2 border-accent px-3 py-3 text-sm flex items-center gap-3 mb-4">
              <GroupLogo url={groupPreview.logo_url} size={40} />
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-accent-foreground">Du wirst eingeladen zu</div>
                <div className="font-bold truncate">{groupPreview.name}</div>
                <div className="text-[11px] text-muted-foreground">{groupPreview.member_count} Mitglieder · Code {urlCode}</div>
              </div>
            </div>
          )}

          {sent ? (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-5xl mb-2">📬</div>
                <p className="font-display font-extrabold text-xl">Fast geschafft!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Wir haben eine Bestätigungs-Mail an<br />
                  <b className="text-foreground break-all">{signupEmail}</b><br />
                  geschickt.
                </p>
              </div>

              <ol className="space-y-2.5 text-sm">
                <li className="flex gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center text-sm">1</span>
                  <span className="pt-0.5">Öffne dein <b>E-Mail-Postfach</b></span>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center text-sm">2</span>
                  <span className="pt-0.5">Klicke auf den <b>Magic Link</b> in der Mail</span>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 h-7 w-7 rounded-full bg-accent text-accent-foreground font-bold flex items-center justify-center text-sm">3</span>
                  <span className="pt-0.5">Du wirst automatisch eingeloggt — danach <b>Passwort & Profil im Onboarding</b> festlegen</span>
                </li>
              </ol>

              <div className="rounded-md bg-muted/50 border border-border p-2.5 text-[11px] text-muted-foreground text-center">
                💡 Schau auch im <b>Spam-Ordner</b> nach — manchmal landet die Mail dort.
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={resend}
                  disabled={resendCooldown > 0}
                  className="flex-1 h-11 text-xs font-bold"
                >
                  {resendCooldown > 0 ? `Erneut in ${resendCooldown}s` : "📬 Nochmal senden"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setSent(false); }}
                  className="flex-1 h-11 text-xs font-bold"
                >
                  Zurück
                </Button>
              </div>
            </div>
          ) : (
            <Tabs defaultValue={urlCode ? "signup" : "login"} className="w-full">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login">Anmelden</TabsTrigger>
                <TabsTrigger value="signup">Registrieren</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={submitPassword} className="space-y-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">E-Mail</label>
                    <Input
                      type="email" required value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="du@example.com"
                      className="mt-1 h-12 text-base"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Passwort</label>
                    <Input
                      type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="mt-1 h-12 text-base"
                    />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base tap">
                    {busy ? "Logge ein …" : "Einloggen 🔓"}
                  </Button>
                  <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                    <DialogTrigger asChild>
                      <button type="button" className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline pt-1">
                        Passwort vergessen?
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>Passwort zurücksetzen</DialogTitle>
                        <DialogDescription>
                          Wir senden dir einen Link, mit dem du ein neues Passwort setzen kannst.
                        </DialogDescription>
                      </DialogHeader>
                      <Input
                        type="email" placeholder="du@example.com"
                        value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                        className="h-12"
                      />
                      <DialogFooter>
                        <Button onClick={sendReset} disabled={resetBusy || !resetEmail} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                          {resetBusy ? "Sende …" : "Reset-Link senden 📬"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={sendSignup} className="space-y-3">
                  <div className="rounded-md bg-accent/10 border border-accent/30 p-2.5 text-[11px] text-foreground/80">
                    📧 Gib deine E-Mail ein — wir senden dir einen Magic Link. Nach dem Klick auf den Link kannst du dein Passwort & Profil im Onboarding festlegen.
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">E-Mail</label>
                    <Input
                      type="email" required value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      placeholder="du@example.com"
                      className="mt-1 h-12 text-base"
                    />
                  </div>
                  <Button type="submit" disabled={signupBusy || !validSignup} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-base tap">
                    {signupBusy ? "Sende …" : "Magic Link senden 📬"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          )}
        </motion.div>
      </div>
      <footer className="text-center text-xs text-primary-foreground/60 pb-4 space-y-2">
        <div>Powered by Englputzeder · englputzeder.at</div>
        <div className="flex justify-center gap-4">
          <a href="/impressum" className="underline hover:text-primary-foreground">Impressum</a>
          <a href="/datenschutz" className="underline hover:text-primary-foreground">Datenschutz</a>
          <a href="/teilnahmebedingungen" className="underline hover:text-primary-foreground">Teilnahmebedingungen</a>
        </div>
        <div className="text-[10px] px-6 max-w-md mx-auto">
          Mit der Registrierung akzeptierst du die <a href="/teilnahmebedingungen" className="underline">Teilnahmebedingungen</a> und die <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
        </div>
        <div className="text-[10px] px-6 max-w-md mx-auto opacity-80">
          Diese Aktion steht in keinerlei Verbindung mit der FIFA oder offiziellen Sponsoren der FIFA Fußball-Weltmeisterschaft 2026.
        </div>
      </footer>
    </div>
  );
}
